/**
 * GLAMRDiP password gate — early access products
 * Joel 2026-05-07 (round 2 — inline-replace pattern).
 *
 * Behaviour:
 *   PDP sections that opt into gating wrap BOTH the gate snippet and
 *   the original ATC markup in [data-gd-atc-wrap] containers, using
 *   `.gd-when-locked` / `.gd-when-unlocked` siblings. Both default-
 *   hidden via CSS in gd-sections.css. This script:
 *     1. On init, reads localStorage[gd_unlocked_<handle>] for each
 *        wrapper's data-product-handle.
 *     2. Adds `.gd-locked-state` or `.gd-unlocked-state` class to the
 *        wrapper, revealing exactly one branch.
 *     3. Wires submit on each [data-gd-password-form] inside any
 *        gate; on correct password, persists + toggles ALL wrappers
 *        sharing that handle (covers inline + sticky in one shot)
 *        + dispatches `gd:product-unlocked` for any downstream
 *        consumer (kit-builder, etc.).
 *
 * Security note: client-side only. Anyone determined can read the
 * SHA-256 hash via DevTools, brute-force a short password, or
 * bypass entirely via console (`document.querySelectorAll('[data-gd-
 * atc-wrap]').forEach(w => { w.classList.remove('gd-locked-state');
 * w.classList.add('gd-unlocked-state'); })`). Friends-and-family
 * early-access only — true gating requires a Shopify Function /
 * Cart Transform.
 */
(() => {
  'use strict';

  const STORAGE_PREFIX = 'gd_unlocked_';

  async function sha256Hex(text) {
    const buf = new TextEncoder().encode(text);
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return [...new Uint8Array(digest)]
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  function isUnlocked(handle) {
    try {
      return localStorage.getItem(STORAGE_PREFIX + handle) === 'true';
    } catch {
      return false;
    }
  }

  function persistUnlock(handle) {
    try {
      localStorage.setItem(STORAGE_PREFIX + handle, 'true');
    } catch {
      // localStorage unavailable (private browsing, quota). Silent —
      // unlock state still applies for the current page load via the
      // in-memory class toggle below.
    }
  }

  function setWrapperState(wrap, unlocked) {
    if (unlocked) {
      wrap.classList.remove('gd-locked-state');
      wrap.classList.add('gd-unlocked-state');
    } else {
      wrap.classList.add('gd-locked-state');
      wrap.classList.remove('gd-unlocked-state');
    }
  }

  function unlockHandle(handle) {
    persistUnlock(handle);
    // A single PDP page can have MULTIPLE wrappers with the same
    // handle (e.g. inline ATC wrapper + sticky bar wrapper). Update
    // ALL of them in one pass.
    document.querySelectorAll('[data-gd-atc-wrap][data-product-handle="' + cssEscape(handle) + '"]').forEach((w) => {
      setWrapperState(w, true);
    });
    document.body.dispatchEvent(new CustomEvent('gd:product-unlocked', {
      bubbles: true,
      detail: { handle }
    }));
  }

  // Tiny CSS.escape polyfill for the attribute selector. Shopify
  // handles are all kebab-case and ASCII-safe so this is overkill in
  // practice, but defensive in case a handle ever contains a
  // selector-special char.
  function cssEscape(value) {
    if (window.CSS && CSS.escape) return CSS.escape(value);
    return String(value).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function initWrapper(wrap) {
    if (wrap.dataset.gdAtcWrapInit === '1') return;
    wrap.dataset.gdAtcWrapInit = '1';
    const handle = wrap.dataset.productHandle;
    if (!handle) return;
    setWrapperState(wrap, isUnlocked(handle));
  }

  function initGate(gate) {
    if (gate.dataset.gdPasswordInit === '1') return;
    gate.dataset.gdPasswordInit = '1';

    const handle = gate.dataset.productHandle;
    const expectedHash = gate.dataset.passwordHash;
    const form = gate.querySelector('[data-gd-password-form]');
    const input = gate.querySelector('[data-gd-password-input]');
    const errorEl = gate.querySelector('[data-gd-password-error]');
    const successEl = gate.querySelector('[data-gd-password-success]');
    if (!form || !input) return;

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (errorEl) errorEl.hidden = true;
      if (successEl) successEl.hidden = true;

      const value = (input.value || '').trim();
      if (!value) return;   // silent no-op — cleaner than firing error

      let attemptHash;
      try {
        attemptHash = await sha256Hex(value);
      } catch {
        // SubtleCrypto unavailable (insecure context, ancient browser).
        // Fail closed.
        if (errorEl) errorEl.hidden = false;
        return;
      }

      if (attemptHash === expectedHash) {
        if (successEl) successEl.hidden = false;
        unlockHandle(handle);
      } else {
        if (errorEl) errorEl.hidden = false;
        input.value = '';
        input.focus();
      }
    });
  }

  function initAll() {
    // Joel 2026-05-07 (perf — defensive): early-exit if no gate
    // markup is on the page. The script normally only loads on
    // tagged-product PDPs (the snippet that injects the <script>
    // is inside the `product.tags contains 'password-protected'`
    // conditional in the calling sections) but this guard makes
    // the init cost a single querySelector call when the script
    // is loaded on a page with no gate (cache mistakes, future
    // refactors, etc.). Cart-drawer reloads dispatch
    // shopify:section:load — which fires initAll on every event;
    // the early-exit keeps that overhead at one cheap query for
    // any page without a gate.
    if (!document.querySelector('[data-gd-atc-wrap]')) return;
    document.querySelectorAll('[data-gd-atc-wrap]').forEach(initWrapper);
    document.querySelectorAll('[data-gd-password-gate]').forEach(initGate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }
  // Re-init on Shopify section editor reload (Customizer save reloads
  // section markup; without this the new wrappers would be uninitialised).
  document.addEventListener('shopify:section:load', initAll);
})();
